import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatNumber, formatFull, formatDuration, timeAgo, formatDateTime, daysSince } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  EyeIcon, ThumbsUpIcon, ChatCircleIcon, SparkleIcon, CircleNotchIcon,
  CheckCircleIcon, WarningIcon, TrophyIcon, ClockIcon, MegaphoneIcon, ArrowsClockwiseIcon,
} from "@phosphor-icons/react";

const ratingColor = (r) => (r >= 70 ? "#A3E635" : r >= 40 ? "#FFFFFF" : "#16C784");

const AnalysisView = ({ video, analysis }) => (
  <div className="space-y-5">
    <div className="flex items-center gap-4">
      <div className="relative w-20 h-20 shrink-0 rounded-full flex items-center justify-center border-2"
        style={{ borderColor: ratingColor(analysis.rating) }}>
        <span className="font-rajdhani font-bold text-2xl" style={{ color: ratingColor(analysis.rating) }}>
          {analysis.rating}
        </span>
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3E635]">// Performance Score</div>
        <p className="font-rajdhani font-semibold text-lg text-white mt-1">{analysis.verdict}</p>
      </div>
    </div>

    <div>
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#16C784] mb-2">
        <TrophyIcon size={14} /> Why It Performs
      </div>
      <ul className="space-y-2">
        {(analysis.why_performing || []).map((w, i) => (
          <li key={i} className="flex gap-2 text-sm text-[#d4d4d8]">
            <span className="text-[#16C784] font-mono">{String(i + 1).padStart(2, "0")}</span>
            <span>{w}</span>
          </li>
        ))}
      </ul>
    </div>

    <div className="grid sm:grid-cols-2 gap-5">
      <div>
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635] mb-2">
          <CheckCircleIcon size={14} /> Strengths
        </div>
        <ul className="space-y-1.5">
          {(analysis.strengths || []).map((s, i) => (
            <li key={i} className="text-sm text-[#d4d4d8] flex gap-2"><span className="text-[#A3E635]">+</span>{s}</li>
          ))}
        </ul>
      </div>
      <div>
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">
          <WarningIcon size={14} /> Improve
        </div>
        <ul className="space-y-1.5">
          {(analysis.improvements || []).map((s, i) => (
            <li key={i} className="text-sm text-[#d4d4d8] flex gap-2"><span className="text-[#A0A0AB]">→</span>{s}</li>
          ))}
        </ul>
      </div>
    </div>

    {analysis.engagement_note && (
      <div className="border border-[#222226] bg-[#050505] p-3">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A0A0AB] mb-1">Engagement Signal</div>
        <p className="text-sm text-[#d4d4d8]">{analysis.engagement_note}</p>
      </div>
    )}
  </div>
);

const VideoList = ({ videos, onAnalyzed, onPromotedChange }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [reanalyzingId, setReanalyzingId] = useState(null);

  const [sortBy, setSortBy] = useState("date");

  const sortedVideos = [...videos].sort((a, b) => {
    if (sortBy === "score") {
      const ra = a.ai_analysis ? a.ai_analysis.rating : -1;
      const rb = b.ai_analysis ? b.ai_analysis.rating : -1;
      if (rb !== ra) return rb - ra;
      return (b.published_at || "").localeCompare(a.published_at || "");
    }
    if (sortBy === "views") {
      return (b.view_count || 0) - (a.view_count || 0);
    }
    return (b.published_at || "").localeCompare(a.published_at || "");
  });

  const togglePromoted = async (video) => {
    const next = !video.promoted;
    try {
      await api.post(`/videos/${video.video_id}/promoted`, { promoted: next });
      onPromotedChange && onPromotedChange(video.video_id, next);
      toast.success(next ? "Marked as promoted — excluded from your baselines & idea suggestions" : "Unmarked as promoted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const handleReanalyze = async (video, e) => {
    e.stopPropagation();
    setReanalyzingId(video.video_id);
    try {
      const res = await api.post(`/ai/analyze/${video.video_id}`);
      onAnalyzed && onAnalyzed(video.video_id, res.data);
      if (open && active?.video_id === video.video_id) setAnalysis(res.data);
      toast.success(`Re-scored with the latest model — new score ${res.data.rating}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-analysis failed");
    } finally {
      setReanalyzingId(null);
    }
  };

  const handleAnalyze = async (video) => {
    setLoadingId(video.video_id);
    try {
      let result = video.ai_analysis;
      if (!result) {
        const res = await api.post(`/ai/analyze/${video.video_id}`);
        result = res.data;
        onAnalyzed && onAnalyzed(video.video_id, result);
      }
      setActive(video);
      setAnalysis(result);
      setOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analysis failed");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <motion.div
      data-testid="video-list"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-[#0C0C0E] border border-[#222226]"
      style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)" }}
    >
      <div className="flex items-center justify-between px-5 md:px-6 py-4 border-b border-[#222226]">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635]">// Upload Log</div>
          <h2 className="font-rajdhani font-bold text-xl tracking-wide mt-1">RECENT VIDEOS</h2>
        </div>
        <span className="font-mono text-xs text-[#5a5a63]">{videos.length} TRACKED</span>
      </div>

      <div className="flex items-center gap-2 px-4 md:px-6 py-3 border-b border-[#1a1a1e]">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[#5a5a63]">Sort by</span>
        <div className="flex border border-[#222226]">
          <button
            data-testid="sort-date"
            onClick={() => setSortBy("date")}
            className={`font-rajdhani uppercase tracking-wider text-xs px-3 py-1.5 transition-colors duration-200 ${sortBy === "date" ? "bg-[#16C784] text-black" : "text-[#A0A0AB] hover:text-white"}`}
          >
            Most recent
          </button>
          <button
            data-testid="sort-score"
            onClick={() => setSortBy("score")}
            className={`font-rajdhani uppercase tracking-wider text-xs px-3 py-1.5 transition-colors duration-200 ${sortBy === "score" ? "bg-[#16C784] text-black" : "text-[#A0A0AB] hover:text-white"}`}
          >
            Performance score
          </button>
          <button
            data-testid="sort-views"
            onClick={() => setSortBy("views")}
            className={`font-rajdhani uppercase tracking-wider text-xs px-3 py-1.5 transition-colors duration-200 ${sortBy === "views" ? "bg-[#16C784] text-black" : "text-[#A0A0AB] hover:text-white"}`}
          >
            Most viewed
          </button>
        </div>
      </div>

      <div className="divide-y divide-[#1a1a1e] max-h-[620px] overflow-y-auto">
        {sortedVideos.map((v) => (
          <div
            key={v.video_id}
            data-testid={`video-row-${v.video_id}`}
            className="flex items-center gap-4 px-4 md:px-6 py-4 hover:bg-white/[0.02] transition-colors duration-200 group"
          >
            <div className="relative shrink-0">
              <img src={v.thumbnail} alt="" className="w-28 md:w-36 aspect-video object-cover border border-[#222226]" />
              {v.duration && (
                <span className="absolute bottom-1 right-1 bg-black/85 text-white font-mono text-[10px] px-1">
                  {formatDuration(v.duration)}
                </span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <a
                href={`https://youtube.com/watch?v=${v.video_id}`}
                target="_blank" rel="noreferrer"
                className="font-medium text-sm md:text-base text-white line-clamp-2 hover:text-[#16C784] transition-colors duration-200"
              >
                {v.title}
              </a>
              <div className="flex items-center gap-4 mt-2 font-mono text-[11px] text-[#A0A0AB]">
                <span className="flex items-center gap-1"><EyeIcon size={13} className="text-[#16C784]" />{formatNumber(v.view_count)}</span>
                <span className="flex items-center gap-1"><ThumbsUpIcon size={13} />{formatNumber(v.like_count)}</span>
                <span className="flex items-center gap-1"><ChatCircleIcon size={13} />{formatNumber(v.comment_count)}</span>
                <span className="hidden sm:inline text-[#5a5a63]" title={formatDateTime(v.published_at)}>{timeAgo(v.published_at)}</span>
              </div>
              <div className="mt-1 font-mono text-[10px] text-[#5a5a63] flex items-center gap-1 flex-wrap">
                <ClockIcon size={11} /> {formatDateTime(v.published_at)}
                <span className="text-[#16C784]">· {daysSince(v.published_at)} {daysSince(v.published_at) === 1 ? "day" : "days"} since posted</span>
                {v.promoted && (
                  <span data-testid={`promoted-badge-${v.video_id}`} className="ml-1 inline-flex items-center gap-1 text-[#A3E635] border border-[#A3E635]/40 px-1.5 py-0.5 uppercase tracking-wider">
                    <MegaphoneIcon size={10} weight="fill" /> Promoted
                  </span>
                )}
              </div>
            </div>

            <div className="shrink-0 flex flex-col items-end gap-2">
              {v.ai_analysis && (
                <div className="flex flex-col items-center px-2 leading-none" title="Content Overwatch performance score (0-100) vs your channel baseline">
                  <span className="font-rajdhani font-bold text-lg" style={{ color: ratingColor(v.ai_analysis.rating) }}>
                    {v.ai_analysis.rating}
                  </span>
                  <span className="font-mono text-[8px] uppercase tracking-[0.15em] text-[#5a5a63]">score</span>
                </div>
              )}
              <button
                data-testid={`analyze-button-${v.video_id}`}
                onClick={() => handleAnalyze(v)}
                disabled={loadingId === v.video_id}
                className="flex items-center gap-1.5 border border-[#16C784]/60 text-[#16C784] hover:bg-[#16C784]/10 disabled:opacity-50 font-rajdhani uppercase tracking-wider text-[11px] px-3 py-1.5 transition-colors duration-200"
              >
                {loadingId === v.video_id ? (
                  <CircleNotchIcon size={13} className="spin-slow" />
                ) : (
                  <SparkleIcon size={13} weight="fill" />
                )}
                {v.ai_analysis ? "View" : "Analyze"}
              </button>
              {v.ai_analysis && (
                <button
                  data-testid={`reanalyze-button-${v.video_id}`}
                  onClick={(e) => handleReanalyze(v, e)}
                  disabled={reanalyzingId === v.video_id}
                  className="flex items-center gap-1.5 border border-[#222226] text-[#A0A0AB] hover:border-[#16C784]/50 hover:text-[#16C784] disabled:opacity-50 font-rajdhani uppercase tracking-wider text-[11px] px-3 py-1.5 transition-colors duration-200"
                  title="Re-score this video with the latest grading model"
                >
                  {reanalyzingId === v.video_id ? (
                    <CircleNotchIcon size={13} className="spin-slow" />
                  ) : (
                    <ArrowsClockwiseIcon size={13} />
                  )}
                  Re-analyze
                </button>
              )}
              <button
                data-testid={`promoted-toggle-${v.video_id}`}
                onClick={() => togglePromoted(v)}
                className={`flex items-center gap-1.5 border font-rajdhani uppercase tracking-wider text-[11px] px-3 py-1.5 transition-colors duration-200 ${
                  v.promoted
                    ? "border-[#A3E635] text-[#A3E635] bg-[#A3E635]/10"
                    : "border-[#222226] text-[#A0A0AB] hover:border-[#A3E635]/50 hover:text-[#A3E635]"
                }`}
                title="Mark this video as promoted (paid) so it doesn't skew your averages or idea suggestions"
              >
                <MegaphoneIcon size={13} weight={v.promoted ? "fill" : "regular"} />
                {v.promoted ? "Promoted" : "Mark promoted"}
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="analysis-dialog"
          className="bg-[#0C0C0E] border border-[#16C784]/40 rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto"
          style={{ boxShadow: "0 0 30px rgba(22, 199, 132,0.15)" }}
        >
          <DialogHeader>
            <DialogTitle className="font-rajdhani font-bold text-xl tracking-wide text-white pr-6 flex items-center gap-2">
              <SparkleIcon size={20} className="text-[#A3E635]" weight="fill" />
              CONTENT OVERWATCH ANALYSIS
            </DialogTitle>
          </DialogHeader>
          {active && <DialogDescription className="text-sm text-[#A0A0AB] -mt-2 mb-2 line-clamp-1">{active.title}</DialogDescription>}
          {active && (
            <button
              data-testid="dialog-reanalyze-button"
              onClick={(e) => handleReanalyze(active, e)}
              disabled={reanalyzingId === active.video_id}
              className="flex items-center gap-1.5 self-start border border-[#16C784]/50 text-[#16C784] hover:bg-[#16C784]/10 disabled:opacity-50 font-rajdhani uppercase tracking-wider text-[11px] px-3 py-1.5 mb-2 transition-colors duration-200"
            >
              {reanalyzingId === active.video_id ? (
                <CircleNotchIcon size={13} className="spin-slow" />
              ) : (
                <ArrowsClockwiseIcon size={13} />
              )}
              Re-analyze with latest model
            </button>
          )}
          {analysis && active && <AnalysisView video={active} analysis={analysis} />}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default VideoList;
